import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Stethoscope, AlertTriangle, Clock, FileText } from 'lucide-react';
import { api, extractApiError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { ROLE_RANK } from '@praxara/shared/constants';

export function PVCasesPage() {
  const user = useAuthStore((s) => s.user);
  const canCreate = user && ROLE_RANK[user.role] >= ROLE_RANK['OPERATOR'];
  const qc = useQueryClient();
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [prefill, setPrefill] = useState<{ sourceDocId?: string; sourceType?: string; sourceChannel?: string } | null>(null);

  useEffect(() => {
    if (searchParams.get('newCase') === '1') {
      setPrefill({
        sourceDocId: searchParams.get('sourceDocId') ?? undefined,
        sourceType: searchParams.get('sourceType') ?? undefined,
        sourceChannel: searchParams.get('sourceChannel') ?? undefined,
      });
      setIntakeOpen(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const cases = useQuery({
    queryKey: ['pv-cases'],
    queryFn: async () => (await api.get('/api/pv/cases?pageSize=100')).data,
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Pharmacovigilance</h1>
          <p className="text-sm text-muted-foreground">
            ICSR intake to MedDRA coding to narrative to QC to submission. Full E2B(R3) export.
          </p>
        </div>
        {canCreate && (
          <Button onClick={() => setIntakeOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" /> New case intake
          </Button>
        )}
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <StatCard icon={Stethoscope} label="Active cases" value={cases.data?.items?.filter((c: PVCase) => c.status !== 'CLOSED' && c.status !== 'REJECTED').length ?? '—'} />
        <StatCard icon={AlertTriangle} label="Expedited" value={cases.data?.items?.filter((c: PVCase) => c.isExpedited).length ?? '—'} />
        <StatCard icon={Clock} label="Awaiting review" value={cases.data?.items?.filter((c: PVCase) => ['CODING','MEDICAL_REVIEW','NARRATIVE','QC'].includes(c.status)).length ?? '—'} />
        <StatCard icon={Stethoscope} label="Submitted" value={cases.data?.items?.filter((c: PVCase) => c.status === 'SUBMITTED' || c.status === 'CLOSED').length ?? '—'} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Cases</CardTitle>
          <CardDescription>
            Serious cases have a 15-day reporting deadline, non-serious 90 days.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Case #</TableHead>
                <TableHead>Product</TableHead>
                <TableHead>Event</TableHead>
                <TableHead>Seriousness</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Due</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cases.isLoading && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">Loading...</TableCell></TableRow>
              )}
              {cases.data?.items?.length === 0 && !cases.isLoading && (
                <TableRow><TableCell colSpan={6} className="text-center text-sm text-muted-foreground">No PV cases yet.</TableCell></TableRow>
              )}
              {cases.data?.items?.map((c: PVCase) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Link to={`/pv/${c.id}`} className="font-medium hover:underline">
                      {c.caseNumber}
                    </Link>
                    {c.sourceDocument && (
                      <span className="ml-1.5 inline-flex items-center text-blue-600" title={`Source: ${c.sourceDocument.fileName}`}>
                        <FileText className="h-3 w-3" />
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{c.suspectProduct}</TableCell>
                  <TableCell className="max-w-xs truncate text-sm">{c.adverseEventText}</TableCell>
                  <TableCell><SeriousnessBadge s={c.seriousness} /></TableCell>
                  <TableCell><StatusBadge s={c.status} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {c.reportingDeadline ? formatDate(c.reportingDeadline) : '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <IntakeDialog
        open={intakeOpen}
        onOpenChange={(o) => { setIntakeOpen(o); if (!o) setPrefill(null); }}
        onCreated={() => qc.invalidateQueries({ queryKey: ['pv-cases'] })}
        prefill={prefill}
      />
    </div>
  );
}

interface PVCase {
  id: string;
  caseNumber: string;
  suspectProduct: string;
  adverseEventText: string;
  seriousness: string | null;
  status: string;
  isExpedited: boolean;
  reportingDeadline: string | null;
  sourceDocument?: { id: string; fileName: string } | null;
}

function SeriousnessBadge({ s }: { s: string | null }) {
  if (!s) return <span className="text-muted-foreground">--</span>;
  if (s === 'non_serious') return <Badge variant="secondary">Non-serious</Badge>;
  if (s === 'death' || s === 'life_threatening') return <Badge variant="destructive">{s.replace('_', ' ')}</Badge>;
  return <Badge variant="warning">{s.replace('_', ' ')}</Badge>;
}

function StatusBadge({ s }: { s: string }) {
  const v: Record<string, 'secondary' | 'info' | 'success' | 'destructive' | 'warning'> = {
    INTAKE: 'secondary',
    TRIAGE: 'info',
    DATA_ENTRY: 'info',
    CODING: 'warning',
    MEDICAL_REVIEW: 'warning',
    NARRATIVE: 'warning',
    QC: 'warning',
    SUBMISSION_READY: 'success',
    SUBMITTED: 'success',
    CLOSED: 'secondary',
    REJECTED: 'destructive',
  };
  return <Badge variant={v[s] ?? 'secondary'}>{s.replace('_', ' ')}</Badge>;
}

function StatCard({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: number | string;
}) {
  return (
    <Card>
      <CardContent className="flex items-center gap-4 p-6">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-primary/10 text-primary">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-xs text-muted-foreground">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
}

function IntakeDialog({
  open,
  onOpenChange,
  onCreated,
  prefill,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  onCreated: () => void;
  prefill?: { sourceDocId?: string; sourceType?: string; sourceChannel?: string } | null;
}) {
  const [form, setForm] = useState({
    caseNumber: '',
    sourceType: prefill?.sourceType ?? 'spontaneous',
    sourceChannel: prefill?.sourceChannel ?? 'email',
    sourceDocumentId: prefill?.sourceDocId ?? '',
    suspectProduct: '',
    adverseEventText: '',
    patientInitials: '',
    patientAgeYears: '',
    patientSex: 'Unknown',
    seriousness: 'non_serious',
  });

  useEffect(() => {
    if (prefill) {
      setForm((f) => ({
        ...f,
        sourceType: prefill.sourceType ?? f.sourceType,
        sourceChannel: prefill.sourceChannel ?? f.sourceChannel,
        sourceDocumentId: prefill.sourceDocId ?? f.sourceDocumentId,
      }));
    }
  }, [prefill]);

  const tenantDocs = useQuery({
    queryKey: ['tenant-docs-for-intake'],
    queryFn: async () => (await api.get('/api/documents?pageSize=200')).data,
    enabled: open,
  });

  const create = useMutation({
    mutationFn: async () => {
      return api.post('/api/pv/cases', {
        ...form,
        receiptDate: new Date().toISOString(),
        patientAgeYears: form.patientAgeYears ? Number(form.patientAgeYears) : undefined,
        sourceDocumentId: form.sourceDocumentId || undefined,
      });
    },
    onSuccess: () => {
      toast.success('Case created', `Case ${form.caseNumber} intaken`);
      onOpenChange(false);
      onCreated();
    },
    onError: (err) => {
      toast.error('Intake failed', extractApiError(err).message);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>New PV case intake</DialogTitle>
          <DialogDescription>
            Record a new adverse event. Reporting deadline auto-computes based on seriousness.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="cn">Case number</Label>
            <Input id="cn" value={form.caseNumber} onChange={(e) => setForm({ ...form, caseNumber: e.target.value })} placeholder="PVX-2026-0001" />
          </div>
          <div>
            <Label htmlFor="src">Source</Label>
            <select id="src" value={form.sourceType} onChange={(e) => setForm({ ...form, sourceType: e.target.value })} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="spontaneous">Spontaneous</option>
              <option value="clinical_trial">Clinical trial</option>
              <option value="literature">Literature</option>
              <option value="solicited">Solicited</option>
              <option value="regulatory_authority">Regulatory authority</option>
            </select>
          </div>
          <div>
            <Label htmlFor="ch">Channel</Label>
            <select id="ch" value={form.sourceChannel} onChange={(e) => setForm({ ...form, sourceChannel: e.target.value })} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="email">Email</option>
              <option value="phone">Phone</option>
              <option value="web_form">Web form</option>
              <option value="literature">Literature</option>
              <option value="social_media">Social media</option>
              <option value="ha_database">HA database</option>
            </select>
          </div>
          <div>
            <Label htmlFor="ser">Seriousness</Label>
            <select id="ser" value={form.seriousness} onChange={(e) => setForm({ ...form, seriousness: e.target.value })} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="non_serious">Non-serious</option>
              <option value="medically_important">Medically important</option>
              <option value="hospitalisation">Hospitalisation</option>
              <option value="life_threatening">Life-threatening</option>
              <option value="disability">Disability</option>
              <option value="death">Death</option>
              <option value="congenital">Congenital</option>
            </select>
          </div>
          <div className="col-span-2">
            <Label htmlFor="srcDoc">Source document (optional)</Label>
            <select
              id="srcDoc"
              value={form.sourceDocumentId}
              onChange={(e) => setForm({ ...form, sourceDocumentId: e.target.value })}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">— No document linked —</option>
              {tenantDocs.data?.items?.map((d: { id: string; fileName: string }) => (
                <option key={d.id} value={d.id}>{d.fileName}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-muted-foreground">Link the original report (article, email, form) for source traceability.</p>
          </div>
          <div className="col-span-2">
            <Label htmlFor="prod">Suspect product</Label>
            <Input id="prod" value={form.suspectProduct} onChange={(e) => setForm({ ...form, suspectProduct: e.target.value })} placeholder="Drug X 100mg" />
          </div>
          <div className="col-span-2">
            <Label htmlFor="ev">Adverse event (verbatim)</Label>
            <Textarea id="ev" rows={3} value={form.adverseEventText} onChange={(e) => setForm({ ...form, adverseEventText: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="pi">Patient initials</Label>
            <Input id="pi" value={form.patientInitials} onChange={(e) => setForm({ ...form, patientInitials: e.target.value })} placeholder="J.D." />
          </div>
          <div>
            <Label htmlFor="age">Age (years)</Label>
            <Input id="age" type="number" value={form.patientAgeYears} onChange={(e) => setForm({ ...form, patientAgeYears: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="sex">Sex</Label>
            <select id="sex" value={form.patientSex} onChange={(e) => setForm({ ...form, patientSex: e.target.value })} className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm">
              <option value="Unknown">Unknown</option>
              <option value="F">Female</option>
              <option value="M">Male</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => create.mutate()} disabled={create.isPending || !form.caseNumber || !form.suspectProduct || !form.adverseEventText}>
            {create.isPending ? 'Creating...' : 'Create case'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
